import { epoch } from './datetime';

interface SvpTokenOptions {
  st: number;
  exp: number;
  acl: string;
  hmac: string;
}

const EXPIRATION_MARGIN_IN_SECONDS = 5;

export class SvpToken {
  payload?: SvpTokenOptions;

  constructor(options?: SvpTokenOptions) {
    this.payload = options;
  }

  isValid() {
    return (
      !this.payload || this.payload.exp - EXPIRATION_MARGIN_IN_SECONDS > epoch()
    );
  }

  toString() {
    if (!this.payload) {
      return '';
    }

    return [
      `st=${this.payload.st}`,
      `exp=${this.payload.exp}`,
      `acl=${this.payload.acl}`,
      `hmac=${this.payload.hmac}`,
    ].join('~');
  }

  async refresh(getTokenImpl: () => Promise<string | null>): Promise<boolean> {
    if (!this.isValid()) {
      try {
        const tokenStr = await getTokenImpl();
        if (tokenStr) {
          const newToken = SvpToken.fromString(tokenStr);
          if (newToken.isValid()) {
            this.payload = newToken.payload;
            return true;
          }
        }
      } catch (err) {
        // noop
      }
    }
    return false;
  }

  static fromPlaylistItem(playlistItem: jwplayer.PlaylistItem) {
    const url = new URL(playlistItem.file);

    const token = url.searchParams.get('hdnea') ?? '';

    return SvpToken.fromString(token);
  }

  static fromString(token: string) {
    if (!token) {
      return new SvpToken();
    }

    const payload = token
      .split('~')
      .map((param) => param.split('='))
      .reduce(
        (object, [key, value]) => {
          return { ...object, [key]: value };
        },
        {} as Record<keyof SvpTokenOptions, string>
      );

    return new SvpToken({
      st: parseInt(payload.st, 10),
      exp: parseInt(payload.exp, 10),
      acl: payload.acl,
      hmac: payload.hmac,
    });
  }
}
